import { useAuth } from '@/lib/auth/useAuth';
import { useUser } from '@/lib/contexts/UserContext';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CollectNameForm } from '@/components/auth/CollectNameForm';
import { AuthDebugger } from '@/components/AuthDebugger';
import { identifyUser as koalaIdentifyUser, trackEvent as koalaTrackEvent } from '@/lib/koala';

export function AuthPage() {
  const { login, logout, isAuthenticated, user: authUser, loading } = useAuth();
  const { user, needsOnboarding, updateUser } = useUser();
  const [needsNameCollection, setNeedsNameCollection] = useState(false);
  const [lastRedirectTime, setLastRedirectTime] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // DEBUG: Log auth state
  if (import.meta.env.DEV) {
    console.log('[AuthPage] Debug state:', {
      isAuthenticated,
      authUser: !!authUser,
      loading,
      user: !!user,
      pathname: location.pathname,
      needsNameCollection,
      needsOnboarding
    });
  }

  const isCallbackRoute = location.pathname === '/auth/callback';

  // Immediate redirect for clearly authenticated users on /auth page (with rate limiting)
  useEffect(() => {
    if (isAuthenticated && user && !loading && location.pathname === '/auth' && !needsNameCollection) {
      const now = Date.now();
      // Rate limit redirects - only allow redirect if last one was more than 2 seconds ago
      if (now - lastRedirectTime > 2000) {
        if (import.meta.env.DEV) {
          console.log('[AuthPage] IMMEDIATE redirect for authenticated user on /auth page');
        }
        setLastRedirectTime(now);
        // Add small delay to prevent rapid redirect loops
        const immediateTimer = setTimeout(() => {
          navigate('/', { replace: true });
        }, 250);
        return () => clearTimeout(immediateTimer);
      } else {
        if (import.meta.env.DEV) {
          console.log('[AuthPage] Skipping redirect due to rate limiting');
        }
      }
    }
  }, [isAuthenticated, user, loading, location.pathname, needsNameCollection, navigate, lastRedirectTime]);

  // Check if we need to collect name after authentication
  useEffect(() => {
    if (authUser && (!authUser.firstName || authUser.firstName.trim() === '')) {
      setNeedsNameCollection(true);
    } else {
      setNeedsNameCollection(false);
    }
  }, [authUser]);

  // Handle navigation based on auth state
  useEffect(() => {
    // Only handle redirects if we're not in a loading state
    if (loading) {
      if (import.meta.env.DEV) {
        console.log('[AuthPage] Skipping redirect due to loading state');
      }
      return;
    }

    // IMPORTANT: Only redirect if authenticated AND have user data
    // This prevents infinite loops when onboarding state is inconsistent
    if (isAuthenticated && user && !needsNameCollection) {
      const now = Date.now();
      // Rate limit redirects to prevent thrashing
      if (now - lastRedirectTime > 1000) {
        if (import.meta.env.DEV) {
          console.log('[AuthPage] Setting up redirect timer for authenticated user:', {
            isCallbackRoute,
            pathname: location.pathname,
            needsNameCollection
          });
        }
        
        // Add delay to prevent immediate redirect loops
        const redirectTimer = setTimeout(() => {
          // If on callback route
          if (isCallbackRoute) {
            if (import.meta.env.DEV) {
              console.log('[AuthPage] Redirecting from callback route to dashboard');
            }
            setLastRedirectTime(Date.now());
            navigate('/', { replace: true });
            return;
          }
          
          // If on auth page but shouldn't be
          if (location.pathname === '/auth') {
            if (import.meta.env.DEV) {
              console.log('[AuthPage] Redirecting authenticated user from auth page to dashboard');
            }
            setLastRedirectTime(Date.now());
            navigate('/', { replace: true });
            return;
          }
        }, 100);
        
        return () => clearTimeout(redirectTimer);
      } else {
        if (import.meta.env.DEV) {
          console.log('[AuthPage] Skipping timed redirect due to rate limiting');
        }
      }
    } else {
      if (import.meta.env.DEV && isAuthenticated) {
        console.log('[AuthPage] Not redirecting because:', {
          isAuthenticated,
          hasUser: !!user,
          needsNameCollection,
          loading
        });
      }
    }
  }, [isAuthenticated, user, loading, location.pathname, needsNameCollection, isCallbackRoute, navigate, lastRedirectTime]);

  // DEBUG: Add timeout safety check with recovery
  useEffect(() => {
    const timeout = setTimeout(() => {
      console.error('[AuthPage] STUCK FOR 10 SECONDS - State:', {
        isAuthenticated,
        authUser: !!authUser,
        loading,
        user: !!user,
        pathname: location.pathname,
        needsNameCollection,
        needsOnboarding
      });
      
      // Force redirect if authenticated but stuck on auth page
      if (isAuthenticated && user && !loading && location.pathname === '/auth') {
        console.error('[AuthPage] Force redirecting authenticated user from auth page');
        navigate('/', { replace: true });
        return;
      }
      
      // Force reload if completely stuck
      if (!loading && !isAuthenticated && !authUser) {
        console.error('[AuthPage] Force reloading due to stuck state');
        window.location.reload();
      }
    }, 10000);
    
    return () => clearTimeout(timeout);
  }, [isAuthenticated, authUser, loading, user, location.pathname, needsNameCollection, needsOnboarding, navigate]);

  // Handle name collection submission
  const completeNameCollection = async (firstName: string, lastName: string) => {
    if (!authUser) return;
    
    try {
      await updateUser({
        id: authUser.id,
        name: `${firstName} ${lastName}`.trim(),
        metadata: {
          firstName,
          lastName
        }
      });
      
      // Identify user in Koala with updated name information
      koalaIdentifyUser(authUser.email, {
        name: `${firstName} ${lastName}`.trim(),
        firstName,
        lastName,
        isAuthenticated: true
      });
      
      // Track name collection completion
      koalaTrackEvent('name_collection_completed', {
        userId: authUser.id,
        email: authUser.email,
        name: `${firstName} ${lastName}`.trim()
      });
      
      setNeedsNameCollection(false);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Failed to update user name:', error);
    }
  };

  // If we're on a callback route, show a loading state
  if (isCallbackRoute && loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Completing sign in...</p>
        </div>
      </div>
    );
  }

  // If we need to collect the user's name, show the name collection form
  if (needsNameCollection && authUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <CollectNameForm
          email={authUser.email}
          onSubmit={completeNameCollection}
        />
      </div>
    );
  }

  // If we're authenticated and don't need name collection, show loading while redirecting
  if (isAuthenticated && !needsNameCollection) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  // Show login form for unauthenticated users
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Welcome to EvalOps
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Please sign in to continue
          </p>
        </div>
        <div className="mt-8 space-y-6">
          <button
            onClick={() => {
              // Track login attempt in Koala
              koalaTrackEvent('login_initiated', {
                method: 'workos',
                page: 'auth'
              });
              login();
            }}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Sign in with WorkOS
          </button>
        </div>
      </div>

      {/* Auth debugger component */}
      <div className="mt-8 max-w-md w-full">
        <AuthDebugger />
      </div>
    </div>
  );
} 